import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { getProjectDashboard } from '../lib/api';
import {
  ChartLine,
  Clock,
  CheckCircle,
  Circle,
  HourglassHigh,
  Users,
  TrendUp,
  CalendarBlank,
  CurrencyDollar,
  FileText,
  DownloadSimple
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { StatusBadge } from '../components/app';

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadProjectDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await getProjectDashboard(projectId);
      setDashboardData(data);
    } catch (error) {
      console.error('Error loading project dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadProjectDashboard();
  }, [loadProjectDashboard]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E2B05E] mx-auto mb-4"></div>
            <p className="text-[#9CA3AF]">Cargando dashboard del proyecto...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!dashboardData) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-[#9CA3AF]">No se pudo cargar la información del proyecto</p>
        </div>
      </Layout>
    );
  }

  const { project, progress, hours, tasks_by_employee, hours_by_employee } = dashboardData;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-['Outfit'] text-3xl font-semibold text-[#F3F4F6] mb-2">
              {project.name}
            </h1>
            <p className="text-[#9CA3AF] text-sm max-w-2xl">{project.description}</p>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge value={project.status} className="px-3 py-1 text-sm" />
            <Button 
              variant="outline"
              onClick={() => {
                import('../utils/exportUtils').then(mod => mod.exportProjectDashboardToPDF(dashboardData));
              }}
              className="border-[#242E42] text-[#9CA3AF]"
            >
              <DownloadSimple className="w-4 h-4 mr-2" />
              PDF
            </Button>
          </div>
        </div>

        {/* Project Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="valhalla-card p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-[#E2B05E]/10 flex items-center justify-center">
                <CalendarBlank className="w-5 h-5 text-[#E2B05E]" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-[#9CA3AF]">Fecha Inicio</p>
                <p className="text-base font-semibold text-[#F3F4F6]">
                  {project.start_date || 'N/A'}
                </p>
              </div>
            </div>
          </div>

          <div className="valhalla-card p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <CalendarBlank className="w-5 h-5 text-blue-400" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-[#9CA3AF]">Fecha Fin</p>
                <p className="text-base font-semibold text-[#F3F4F6]">
                  {project.end_date || 'N/A'}
                </p>
              </div>
            </div>
          </div>

          <div className="valhalla-card p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <CurrencyDollar className="w-5 h-5 text-emerald-400" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-[#9CA3AF]">Presupuesto</p>
                <p className="text-base font-semibold text-[#F3F4F6]">
                  ${project.budget?.toLocaleString() || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="valhalla-card p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <TrendUp className="w-5 h-5 text-purple-400" />
              </div>
              <div className="flex-1">
                <p className="text-xs text-[#9CA3AF]">Progreso</p>
                <p className="text-base font-semibold text-[#F3F4F6]">
                  {progress.progress_percentage}%
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="valhalla-card p-6">
            <div className="flex items-center gap-3 mb-3">
              <FileText className="w-6 h-6 text-[#E2B05E]" weight="duotone" />
              <p className="text-sm text-[#9CA3AF]">Total Tareas</p>
            </div>
            <p className="text-3xl font-bold text-[#F3F4F6]">{progress.total_tasks}</p>
          </div>

          <div className="valhalla-card p-6">
            <div className="flex items-center gap-3 mb-3">
              <CheckCircle className="w-6 h-6 text-emerald-400" weight="duotone" />
              <p className="text-sm text-[#9CA3AF]">Completadas</p>
            </div>
            <p className="text-3xl font-bold text-emerald-400">{progress.completed_tasks}</p>
          </div>

          <div className="valhalla-card p-6">
            <div className="flex items-center gap-3 mb-3">
              <HourglassHigh className="w-6 h-6 text-blue-400" weight="duotone" />
              <p className="text-sm text-[#9CA3AF]">En Progreso</p>
            </div>
            <p className="text-3xl font-bold text-blue-400">{progress.in_progress_tasks}</p>
          </div>

          <div className="valhalla-card p-6">
            <div className="flex items-center gap-3 mb-3">
              <Circle className="w-6 h-6 text-yellow-400" weight="duotone" />
              <p className="text-sm text-[#9CA3AF]">Pendientes</p>
            </div>
            <p className="text-3xl font-bold text-yellow-400">{progress.pending_tasks}</p>
          </div>
        </div>

        {/* Hours Summary */}
        <div className="valhalla-card p-6">
          <h2 className="font-['Outfit'] text-xl font-semibold text-[#F3F4F6] mb-4 flex items-center gap-2">
            <Clock className="w-6 h-6 text-[#E2B05E]" weight="duotone" />
            Resumen de Horas
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-[#0B0E14] rounded-lg p-4 border border-[#242E42]">
              <p className="text-sm text-[#9CA3AF] mb-1">Total Horas</p>
              <p className="text-2xl font-bold text-[#F3F4F6]">{hours.total_hours}h</p>
            </div>
            <div className="bg-[#0B0E14] rounded-lg p-4 border border-emerald-500/30">
              <p className="text-sm text-[#9CA3AF] mb-1">Horas Facturables</p>
              <p className="text-2xl font-bold text-emerald-400">{hours.billable_hours}h</p>
            </div>
            <div className="bg-[#0B0E14] rounded-lg p-4 border border-gray-500/30">
              <p className="text-sm text-[#9CA3AF] mb-1">Horas No Facturables</p>
              <p className="text-2xl font-bold text-gray-400">{hours.non_billable_hours}h</p>
            </div>
          </div>
        </div>

        {/* Tasks by Employee */}
        <div className="valhalla-card p-6">
          <h2 className="font-['Outfit'] text-xl font-semibold text-[#F3F4F6] mb-4 flex items-center gap-2">
            <Users className="w-6 h-6 text-[#E2B05E]" weight="duotone" />
            Tareas por Empleado
          </h2>
          <div className="space-y-4">
            {tasks_by_employee.map((emp) => (
              <div key={emp.employee_id} className="bg-[#0B0E14] rounded-lg p-4 border border-[#242E42]">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-semibold text-[#F3F4F6]">{emp.employee_name}</p>
                    <p className="text-sm text-[#9CA3AF]">{emp.position}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-[#9CA3AF]">Total Tareas</p>
                    <p className="text-xl font-bold text-[#E2B05E]">{emp.total_tasks}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="text-center p-2 bg-green-500/10 rounded border border-green-500/30">
                    <p className="text-xs text-green-400">Completadas</p>
                    <p className="text-lg font-bold text-green-400">{emp.completed_tasks}</p>
                  </div>
                  <div className="text-center p-2 bg-blue-500/10 rounded border border-blue-500/30">
                    <p className="text-xs text-blue-400">En Progreso</p>
                    <p className="text-lg font-bold text-blue-400">{emp.in_progress_tasks}</p>
                  </div>
                  <div className="text-center p-2 bg-yellow-500/10 rounded border border-yellow-500/30">
                    <p className="text-xs text-yellow-400">Pendientes</p>
                    <p className="text-lg font-bold text-yellow-400">{emp.pending_tasks}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  {emp.tasks.slice(0, 5).map((task, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-[#151B26]/50 rounded">
                      <div className="flex-1">
                        <p className="text-sm text-[#F3F4F6]">{task.title}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <StatusBadge value={task.status} className="px-2 py-1 text-xs" />
                        <span className="text-xs text-[#9CA3AF]">
                          {task.actual_hours}h / {task.estimated_hours}h
                        </span>
                      </div>
                    </div>
                  ))}
                  {emp.tasks.length > 5 && (
                    <p className="text-xs text-[#9CA3AF] text-center">
                      +{emp.tasks.length - 5} tareas más
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Hours by Employee */}
        <div className="valhalla-card p-6">
          <h2 className="font-['Outfit'] text-xl font-semibold text-[#F3F4F6] mb-4 flex items-center gap-2">
            <ChartLine className="w-6 h-6 text-[#E2B05E]" weight="duotone" />
            Horas por Empleado
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#242E42]">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-[#9CA3AF]">Empleado</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-[#9CA3AF]">Total Horas</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-[#9CA3AF]">Facturables</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-[#9CA3AF]">No Facturables</th>
                </tr>
              </thead>
              <tbody>
                {hours_by_employee.map((emp) => (
                  <tr key={emp.employee_id} className="border-b border-[#242E42]/50 hover:bg-[#151B26]/50 transition-colors">
                    <td className="py-3 px-4 text-[#F3F4F6]">{emp.employee_name}</td>
                    <td className="py-3 px-4 text-right font-semibold text-[#E2B05E]">
                      {emp.total_hours.toFixed(2)}h
                    </td>
                    <td className="py-3 px-4 text-right text-emerald-400">
                      {emp.billable_hours.toFixed(2)}h
                    </td>
                    <td className="py-3 px-4 text-right text-gray-400">
                      {emp.non_billable_hours.toFixed(2)}h
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}
